import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { getEffectiveFieldMode, useFieldMode } from '../../auth/FieldModeContext';
import { NavIcon } from './NavIcons';
import { isMobileNavActive, isTechnicianShellActive, resolveMobileDock } from './navLinks';
import './app-shell.css';

function MobileDock(): React.JSX.Element | null {
    const { pathname } = useLocation();
    const { user } = useAuth();
    const { fieldMode } = useFieldMode();
    const [moreOpen, setMoreOpen] = useState(false);

    const effectiveFieldMode = getEffectiveFieldMode(fieldMode);
    const config = useMemo(
        () => resolveMobileDock(pathname, user?.role, effectiveFieldMode),
        [pathname, user?.role, effectiveFieldMode]
    );
    const techShell = isTechnicianShellActive(user?.role, effectiveFieldMode);

    const moreActive = useMemo(() => {
        if (!config) return false;
        return config.moreLinks.some((item) => isMobileNavActive(pathname, item.to));
    }, [config, pathname]);

    useEffect(() => {
        setMoreOpen(false);
    }, [pathname]);

    useEffect(() => {
        if (!moreOpen) return undefined;
        document.body.classList.add('app-mobile-more-open');
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setMoreOpen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => {
            document.body.classList.remove('app-mobile-more-open');
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [moreOpen]);

    if (!config) return null;

    const dockLinkClass = (to: string) =>
        isMobileNavActive(pathname, to)
            ? 'app-mobile-dock-link app-mobile-dock-link--active'
            : 'app-mobile-dock-link';

    const moreOverlay =
        moreOpen && typeof document !== 'undefined'
            ? createPortal(
                  <>
                      <button
                          type="button"
                          className="app-mobile-more-backdrop"
                          aria-label="Close menu"
                          onClick={() => setMoreOpen(false)}
                      />
                      <div className="app-mobile-more-sheet" role="dialog" aria-label="More navigation">
                          <div className="app-mobile-more-header">
                              <p className="app-mobile-more-kicker">More</p>
                              <p className="app-mobile-more-title">All modules</p>
                          </div>
                          <div className="app-mobile-more-list">
                              {config.moreLinks.map((item) => (
                                  <NavLink
                                      key={item.to}
                                      to={item.to}
                                      className={
                                          isMobileNavActive(pathname, item.to)
                                              ? 'app-mobile-more-item app-mobile-more-item--active'
                                              : 'app-mobile-more-item'
                                      }
                                      onClick={() => setMoreOpen(false)}
                                  >
                                      <span className="app-mobile-more-item-icon" aria-hidden>
                                          <NavIcon id={item.icon} />
                                      </span>
                                      <span className="app-mobile-more-item-text">
                                          <span className="app-mobile-more-item-label">{item.label}</span>
                                      </span>
                                  </NavLink>
                              ))}
                          </div>
                      </div>
                  </>,
                  document.body
              )
            : null;

    return (
        <>
            {moreOverlay}
            <nav className={`app-mobile-dock${techShell ? ' app-mobile-dock--technician' : ''}`} aria-label="Quick navigation">
                <div className="app-mobile-dock-inner">
                    {config.tabs.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.to.endsWith('/dashboard')}
                            title={item.label}
                            className={dockLinkClass(item.to)}
                        >
                            <span className="app-mobile-dock-icon" aria-hidden>
                                <NavIcon id={item.icon} />
                            </span>
                            <span className="app-mobile-dock-text">{item.shortLabel}</span>
                        </NavLink>
                    ))}
                    <button
                        type="button"
                        title="More"
                        aria-expanded={moreOpen}
                        aria-haspopup="dialog"
                        className={
                            moreOpen || moreActive
                                ? 'app-mobile-dock-link app-mobile-dock-link--active app-mobile-dock-link--more'
                                : 'app-mobile-dock-link app-mobile-dock-link--more'
                        }
                        onClick={() => setMoreOpen((open) => !open)}
                    >
                        <span className="app-mobile-dock-icon" aria-hidden>
                            <NavIcon id="more" />
                        </span>
                        <span className="app-mobile-dock-text">More</span>
                    </button>
                </div>
            </nav>
        </>
    );
}

export default MobileDock;
